import { useClaims } from '@backoffice-frontend/authentication'
import { formatDateTime, getLanguage } from '@backoffice-frontend/common'
import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  LinearProgress,
  List,
  ListItem,
  MoiaButton,
  MoiaToastMessage,
} from '@backoffice-frontend/patterns'
import { useGetServiceAreasQuery } from '@backoffice-frontend/service-area-picker'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import { ChevronDownIcon } from '@moia-dev/pace-icons'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AccountDivider, AccountLayout } from './components/AccountLayout'
import { usePermissionsViewQuery } from './PermissionsView.hook'

export const PermissionsView = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { data: serviceAreasData } = useGetServiceAreasQuery()
  const { t, i18n } = useTranslation()

  const { user, loading: groupsLoading } = useBackofficeLoggedInUser()
  const claims = useClaims()

  const { data, loading: queryLoading } = useErrorHandlingQuery(
    usePermissionsViewQuery,
    { variables: { email: claims.cognito.email() } },
  )

  const filteredServiceAreas = serviceAreasData?.serviceAreas.filter(it =>
    claims.boum.serviceAreas().includes(it.uuid),
  )

  const organization = data?.organizations.find(
    org => org.id === user?.organizationId,
  )

  const [rights, setRights] = useState<string[]>([])
  useEffect(() => {
    setRights(claims.boum.userRights().toJSON().sort())
  }, [claims])

  const groupsApprover = user?.groupsApprover
  const { customerTenants, operatorTenants, ridepoolingServiceTenants } =
    claims.boum

  const handleCopyPermissions = () => {
    const content = {
      organization: organization?.name,
      operatorTenants: operatorTenants(),
      customerTenants: customerTenants(),
      ridepoolingServiceTenants: ridepoolingServiceTenants(),
      fleets: data?.fleets?.map(fleet => fleet.name),
      serviceAreas: filteredServiceAreas?.map(
        serviceArea =>
          serviceArea.displayNames[getLanguage(i18n.language)] ??
          serviceArea.id,
      ),
      groups: user?.timedGroups?.map(group => group.groupName) ?? [],
      rights,
      groupsApprover,
    }

    navigator.clipboard.writeText(JSON.stringify(content, null, 2))

    enqueueSnackbar('success', {
      persist: false,
      anchorOrigin: {
        horizontal: 'center',
        vertical: 'bottom',
      },
      content: function SnackbarContent() {
        return (
          <div>
            <MoiaToastMessage
              message={t(
                'authenticatedAppView_copyRightsAndPermissionsSuccessful',
              )}
              severity={'success'}
            />
          </div>
        )
      },
    })
  }

  const resolveGroupDisplayName = (groupName: string) =>
    data?.groups?.find(el => el.name === groupName)?.displayName ?? groupName

  return (
    <>
      <AccountLayout
        header={t('authenticatedAppView_permissions_header')}
        subheader={t('authenticatedAppView_permissions_caption')}
      >
        <div>
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDownIcon />}>
              {t('authenticatedAppView_yourOrganization')}
            </AccordionSummary>
            <AccordionDetails>
              <List>{organization?.name ?? '-'}</List>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDownIcon />}>
              {t('authenticatedAppView_yourOperatorTenants')}
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {operatorTenants().map(tenant => (
                  <ListItem key={tenant}>{tenant}</ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDownIcon />}>
              {t('authenticatedAppView_yourCustomerTenants')}
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {customerTenants().map(tenant => (
                  <ListItem key={tenant}>{tenant}</ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDownIcon />}>
              {t('authenticatedAppView_yourRidepoolingServiceTenants')}
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {ridepoolingServiceTenants().map(tenant => (
                  <ListItem key={tenant}>{tenant}</ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          {!queryLoading && (
            <Accordion>
              <AccordionSummary expandIcon={<ChevronDownIcon />}>
                {t('authenticatedAppView_yourFleets')}
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {data?.fleets?.map(fleet => (
                    <ListItem key={fleet.id}>{fleet.name}</ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDownIcon />}>
              {t('authenticatedAppView_yourServiceAreas')}
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {filteredServiceAreas?.map(serviceArea => (
                  <ListItem key={serviceArea.uuid}>
                    {serviceArea.displayNames[getLanguage(i18n.language)] ??
                      serviceArea.id}
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDownIcon />}>
              {t('authenticatedAppView_yourGroups')}
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {groupsLoading && <LinearProgress />}
                {user?.timedGroups?.map(group => (
                  <ListItem key={group.groupName}>
                    {resolveGroupDisplayName(group.groupName)}{' '}
                    {group.expires &&
                      `${t('common_expires_label')} ${formatDateTime(group.expires)}`}
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDownIcon />}>
              {t('authenticatedAppView_yourRights')}
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                {rights.map(right => (
                  <ListItem key={right}>{right}</ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDownIcon />}>
              {t('authenticatedAppView_groupsYouApprove')}
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {groupsApprover?.map(group => (
                  <ListItem key={group}> {group}</ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          <div
            css={theme => ({
              marginTop: theme.spacing(2),
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
            })}
          >
            <MoiaButton onClick={handleCopyPermissions}>
              {t('authenticatedAppView_copyRightsAndPermissions_button')}
            </MoiaButton>
          </div>
        </div>
      </AccountLayout>
      <AccountDivider />
    </>
  )
}
